import React, { useState } from 'react';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import { StorageCheckoutStepProps } from '@root/components/checkout/types';
import { useTrack } from '@root/initializers/wt';
import { ClientAddress } from '@root/resources/types/address';

import { FieldAddress } from '@shared/field_address';
import { FieldText } from '@shared/field_text';

import { CHALLENGER as T } from '../tokens';

/** Matches `useBreakpoints().isDesktop` (MD_INT = 840). */
const DESKTOP = '@media (min-width: 840px)';
const MOBILE = '@media (max-width: 839px)';

/** Figma 931:46775 mobile address error copy. */
const INCOMPLETE_ADDRESS_ERROR =
  'Enter a complete address, including street, city, and ZIP code.';

const Fields = styled.div`
  display: grid;
  width: 100%;
  gap: ${T.space[12]};

  ${MOBILE} {
    grid-template-columns: 1fr;
    gap: ${T.space[8]};
  }

  /* Figma: Address flex + Apt/suite fixed ~164px. */
  ${DESKTOP} {
    grid-template-columns: minmax(0, 1fr) 164px;
  }
`;

const FloatLabel = styled.span<{
  $error?: boolean;
  $raised?: boolean;
  $brand?: boolean;
}>`
  position: absolute;
  left: 11px;
  top: -9px;
  z-index: 2;
  padding: 0 4px;
  background: ${T.color.bgPrimary};
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${({ $error, $brand }) =>
    $error
      ? T.color.textError
      : $brand
        ? T.color.borderBrand
        : T.color.textTertiary};
  pointer-events: none;
  opacity: ${({ $raised }) => ($raised ? 1 : 0)};
  transition:
    opacity 0.12s ease,
    color 0.12s ease;

  ${MOBILE} {
    top: -8px;
    font-size: 10px;
    line-height: 14px;
  }
`;

/**
 * Figma 707:10449 / 931:46775 — floating label over 54px (desktop) / 48px (mobile) input.
 * Figma 707:21907 — ZIP-updated success keeps brand border + label (not only on focus).
 * Focus chrome via :focus-within so Downshift select-on-blur is preserved.
 */
const FloatWrap = styled.div<{
  $error?: boolean;
  $raised?: boolean;
  $brand?: boolean;
}>`
  position: relative;
  width: 100%;

  input,
  [role='combobox'],
  .pac-target-input {
    min-height: 54px;
    border-radius: ${T.radius.sm} !important;
    border-width: 1px !important;
    border-color: ${({ $error, $brand }) =>
      $error
        ? T.color.textError
        : $brand
          ? T.color.borderBrand
          : T.color.borderDefault} !important;
    font-family: ${T.font.sans};
    font-size: 16px;
    font-weight: ${FontWeight.Regular};
    line-height: 24px;
    box-sizing: border-box;
    box-shadow: none !important;

    ${MOBILE} {
      min-height: 48px;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.3px;

      /* iOS Safari zooms inputs < 16px; Android can keep Figma 14px. */
      @supports (-webkit-touch-callout: none) {
        font-size: 16px;
        line-height: 24px;
        letter-spacing: normal;
      }
    }
  }

  input:focus,
  [role='combobox']:focus,
  .pac-target-input:focus {
    box-shadow: none !important;
  }

  &:focus-within input,
  &:focus-within [role='combobox'],
  &:focus-within .pac-target-input {
    border-color: ${({ $error }) =>
      $error ? T.color.textError : T.color.borderBrand} !important;
  }

  &:focus-within ${FloatLabel} {
    opacity: 1;
    color: ${({ $error }) =>
      $error ? T.color.textError : T.color.borderBrand};
  }

  /* Hide in-field placeholder once the floating label is up (focus or filled). */
  ${({ $raised }) =>
    $raised
      ? `
    input::placeholder {
      color: transparent;
    }
  `
      : ''}

  &:focus-within input::placeholder {
    color: transparent;
  }

  /*
   * Helper under Address — Figma 931:46775 error copy is Regular 12/16 (not Medium).
   * Overrides FieldAutocomplete’s Text.Caption Medium.
   */
  [data-test-id='address-search'] > div:nth-of-type(2) {
    height: auto;
    min-height: 16px;
    padding: 0;
    color: ${T.color.textError} !important;
    font-family: ${T.font.sans};
    font-size: 12px;
    font-weight: ${FontWeight.Regular} !important;
    line-height: 16px;

    ${MOBILE} {
      font-size: 10px;
      line-height: 14px;
    }

    p,
    span,
    * {
      color: inherit !important;
      font-family: inherit !important;
      font-size: inherit !important;
      font-weight: inherit !important;
      line-height: inherit !important;
    }
  }
`;

/**
 * Figma 707:21907 — Medium 12/16 text-brand-dark under Address after ZIP change.
 * Replaces classic “We’ve located address” helper.
 */
const ZipUpdatedHelper = styled.p`
  margin: 0;
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${T.color.textBrandDark};

  ${MOBILE} {
    font-size: 10px;
    line-height: 14px;
  }
`;

/**
 * Figma Desktop 707:10154 / 707:10449 / 707:21907 / Mobile 931:46775 address row.
 * Empty + filled = neutral chrome (no “We’ve located address”).
 * ZIP change = brand border/label + “ZIP updated to {zip}. Pricing now reflects…”
 * Error = red border + floating label + Regular-weight helper copy.
 */
export const ChallengerAddressFields: React.FC<
  StorageCheckoutStepProps & { onZipChanged?(matchesOriginal: boolean): void }
> = ({ onChange, onZipChanged, values: { address, zip } }) => {
  const track = useTrack({ action: 'input' });
  const [originalZIP] = useState(zip);
  const [error, setError] = useState<string>();
  const [updatedZip, setUpdatedZip] = useState<string>();

  const addressRaised = Boolean(
    address?.street && String(address.street).trim(),
  );
  const aptValue = address?.aptsuite ?? '';
  const aptRaised = Boolean(String(aptValue).trim());
  const zipUpdated = Boolean(updatedZip) && !error;

  const handleAddressChange = (_: 'address', value: Partial<ClientAddress>) => {
    setError(undefined);

    if (
      value.googleAddressType === 'route' ||
      value.googleAddressType === '' ||
      !value.zip
    ) {
      setError(INCOMPLETE_ADDRESS_ERROR);
      setUpdatedZip(undefined);
      onChange('address', undefined);
      return;
    }

    const normalizedZip = value.zip.replace(/\s+/g, '').toUpperCase();
    const normalizedOriginal = (originalZIP ?? '')
      .replace(/\s+/g, '')
      .toUpperCase();
    const zipChangedFromOriginal = normalizedZip !== normalizedOriginal;
    setUpdatedZip(zipChangedFromOriginal ? normalizedZip : undefined);

    if (value.zip !== zip) {
      onChange('zip', value.zip);
      // ZIP-change clears selected dates in PickupSection.
      onZipChanged?.(zipChangedFromOriginal);
    }

    const formattedAddress = `${value.street}, ${value.city}, ${value.state} ${value.zip}`;

    track({
      objectName: 'address',
      value: formattedAddress,
      objectType: 'input:text',
      label: 'address',
    });
    onChange('address', value);
  };

  return (
    <Fields>
      <FloatWrap $error={!!error} $raised={addressRaised} $brand={zipUpdated}>
        <FloatLabel
          $error={!!error}
          $raised={addressRaised}
          $brand={zipUpdated}
        >
          Address
        </FloatLabel>
        <FieldAddress
          testId="address-search"
          value={address}
          placeholder="Address"
          onChange={handleAddressChange}
          onInputChange={(street) => {
            if (street === '') {
              onChange('address', {});
              setUpdatedZip(undefined);
              setError(undefined);
            }
          }}
          /* Figma 707:10154 / 707:10449 — no clear (X) on default or filled. */
          hideClearButton
          includeCityInInput={true}
          field="address"
          error={error}
          selectFirstOnBlur={true}
          inputProps={{ autoComplete: 'off' }}
          initialPrompt={null}
          hideResultsPrompt
          hideIcon
          hideValidState
        />
        {zipUpdated && updatedZip && (
          <ZipUpdatedHelper>
            ZIP updated to {updatedZip}. Pricing now reflects your address.
          </ZipUpdatedHelper>
        )}
      </FloatWrap>
      <FloatWrap $raised={aptRaised}>
        <FloatLabel $raised={aptRaised}>Apt/suite</FloatLabel>
        <FieldText
          name="aptsuite"
          placeholder="Apt/suite"
          onChange={(_, value) =>
            onChange('address', { ...address, aptsuite: value })
          }
          value={aptValue}
          autoComplete="user_typed_aptsuite"
          collapseCaptionWhenEmpty
        />
      </FloatWrap>
    </Fields>
  );
};
